/* eslint-disable no-unused-vars */
import _ from 'lodash';
import { isMobileOnly } from 'mobile-device-detect';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Header,
    Icon, Label,
    Popup
} from "semantic-ui-react";
import { endpoint } from '../../api/http.js';
import { Addons, Calendar, CurrencyExchange, Hotels, Reservations } from "../../api/services/next.js";
import ExtraAddonsPopup from '../../components/ExtraAddonsPopup/index.jsx';
import { dateFormat } from '../../components/shared/constants.js';
import { EditButton } from '../../components/shared/icons.jsx';
import { onError, onWarning } from "../../components/shared/messages.jsx";
import { formatValueAsCurrency } from "../../components/shared/utils.js";
import useBoolean from "../../hooks/useBoolean.jsx";``
import { useTextResources } from '../../language.js';
import './style.less';
import { useLoader } from '../../hooks/useLoader.jsx';
import { GetReservationGroups } from '../../api/services/pms';

const AddonReservationsDetails = (props) => {
    const text = useTextResources()
    const loader = useLoader()

    const [reservations, setReservations] = useState([])
    const [reservationsAddons, setReservationsAddons] = useState([])
    const [reservationDailyAccommodations, setReservationDailyAccommodations] = useState([])

    const [addon, setAddon] = useState()
    const [hotel, setHotel] = useState()
    const [currencyExchange, setCurrencyExchange] = useState([]);
    const [reservationsGroups, setReservationsGroups] = useState([]);
    const [selectedReservation, setSelectedReservation] = useState([])

    const {
        setFalse: closeExtraAddons,
        setTrue: openExtraAddons,
        value: isOpenExtraAddons
    } = useBoolean()

    useEffect(() => {
        loader(true);
        (async () => {
            try {
                const reservations = getReservations({
                    hotelId: props.hotelId,
                    date: moment(props.date).toISOString(),
                    chainAddonId: props.chainAddonId,
                })
                const addons = getAddon({
                    hotelId: props.hotelId,
                    chainAddonId: props.chainAddonId,
                })
                const currencyExchange = getCurrencyExchange({
                    hotelId: props.hotelId,
                    date: moment(props.date).toISOString(),
                })
                const hotel = getHotel({
                    hotelId: props.hotelId
                })

                await Promise.all([
                    reservations,
                    addons,
                    currencyExchange,
                    hotel
                ])
            } catch (e) {
                onError(e);
            } finally {
                loader(false);
            }
        })();
    }, [props.hotelId, props.date, props.chainAddonId])

    const getReservations = useCallback(async (spec) => {
        const reservations = await Calendar.GetReservations({
            HotelId: spec.hotelId,
            ReservationDailyAddonDate: spec.date,
            ChainAddonId: spec.chainAddonId,
            includes: [
                'ReservationGroupReservationReservations',
                'ReservationDailyAccommodations',
                'ReservationDailyAddons',
            ],
        })

        if (reservations?.length > 0) {
            const filteredReservations = reservations.filter(reservation =>
                reservation.ReservationDailyAddons?.length > 0 &&
                reservation.ReservationDailyAddons.some(rda =>
                    moment(rda.Date).isSame(props.date, 'day') &&
                    rda.HotelAddon.ChainAddonId === props.chainAddonId))

            const reservationsGroups = await GetReservationGroups({
                HotelId: spec.hotelId,
                ReservationIds: filteredReservations.map(x => x.Id)
            })

            const reservationsAddons = _.flatMap(
                filteredReservations.filter(reservation => 
                  (!props.roomTypeIds?.length || props.roomTypeIds.includes(reservation.RoomTypeId))
                ),
                reservation => reservation.ReservationDailyAddons.filter(rda =>
                  moment(rda.Date).isSame(props.date, 'day') &&
                  rda.HotelAddon.ChainAddonId === props.chainAddonId
                )
              )

            const reservationsDailyAccommodations = _.flatMap(filteredReservations,
                (reservation => reservation.ReservationDailyAccommodations.filter(rda =>
                    moment(rda.Date).isSame(props.date, 'day'))
                ))

            setReservations(filteredReservations)
            setReservationsAddons(reservationsAddons)
            setReservationDailyAccommodations(reservationsDailyAccommodations)
            setReservationsGroups(reservationsGroups)
        }
    }, [])

    const getAddon = useCallback(async (spec) => {
        const addons = await Addons.GetHotelChainAddons({
            HotelId: spec.hotelId,
            Id: spec.chainAddonId,
            IsActive: true
        })

        if (addons?.length) {
            const addon = addons[0]
            setAddon(addon)
        }

    }, [])

    const getCurrencyExchange = useCallback(async (spec) => {
        const currencyExchange = await CurrencyExchange.GetHotelCurrencyExchangeRates({
            HotelId: spec.hotelId,
            Date: spec.date,
        })
        setCurrencyExchange(currencyExchange)
    }, [])

    const getHotel = useCallback(async (spec) => {
        const hotels = await Hotels.GetHotels({
            Id: spec.hotelId,
        })
        if (hotels?.length) {
            const hotel = hotels[0]
            setHotel(hotel)
        }
    }, [])

    const getTotal = useCallback(() => {
        let total = 0;

        if (reservations?.length && currencyExchange && hotel) {
            const reservationsAddons = _.chain(reservations)
                .flatMap(reservation => reservation.ReservationDailyAddons.filter(rda =>
                    moment(rda.Date).isSame(props.date, 'day') &&
                    rda.HotelAddon.ChainAddonId === props.chainAddonId))
                .value()

            total = _.sumBy(reservationsAddons, formatAddonTotal)
        }
        return total;
    }, [reservations, currencyExchange, hotel])

    const formatAddonTotal = useCallback((reservationAddon) => {
        if (reservationAddon.CurrencyId !== hotel.MainCurrencyId) {
            const exchange = currencyExchange.find(ce => ce.CurrencyId === reservationAddon.CurrencyId);
            if (exchange) {
                return reservationAddon.ComputedTotalAfterTax * exchange.ExchangeRate
            }
        }
        return reservationAddon.ComputedTotalAfterTax
    }, [currencyExchange, hotel])

    const formatGuests = (reservationDailyAccommodation) => {
        return `${reservationDailyAccommodation?.Adults || 0}/${reservationDailyAccommodation?.Children || 0}/${reservationDailyAccommodation?.Infants || 0}`
    }

    const SumQuantity = (reservationAddon) => {
        if (
          !reservationAddon?.AdultQuantity &&
          !reservationAddon?.ChildQuantity &&
          !reservationAddon?.InfantQuantity
        ) {
          return reservationAddon?.Quantity ?? 1;
        }
      
        return (
          (reservationAddon?.AdultQuantity ?? 0) +
          (reservationAddon?.ChildQuantity ?? 0) +
          (reservationAddon?.InfantQuantity ?? 0)
        );
      };

    const isGroupReservation = useCallback((reservation) => {
        if (!reservation?.ReservationGroupReservationReservations?.length || !reservationsGroups?.length) {
            return false;
        }

        const group = reservationsGroups
            .filter(rg => rg.ReservationGroupReservations?.length > 0)
            .find(rg => rg.ReservationGroupReservations.some(x => x.ReservationId === reservation.Id))

        return !group?.IsPartyGroup;
    }, [reservationsGroups])

    const onOpenExtraAddonsPopup = useCallback(async (reservationId) => {
        const reservationResult = await Reservations.GetReservation(reservationId)

        if (!reservationResult || (Array.isArray(reservationResult) && reservationResult.length === 0)) {
            onWarning("Reservation not found")
            return;
        }
        
        // If the API returns an array, get the first item, otherwise use the result directly
        const reservation = Array.isArray(reservationResult) ? reservationResult[0] : reservationResult;
        setSelectedReservation(reservation);
        openExtraAddons()
    }, [])

    const onCloseExtraAddonsPopup = () => {
        closeExtraAddons()
    }

    const RenderTitle = useCallback(({ label }) => (
        <td>
            <Popup
                content={label}
                trigger={
                    <Header size='tiny'>
                        <Header.Subheader>{label}</Header.Subheader>
                    </Header>
                }
            />
        </td>
    ), [])

    const reservationsTable = useMemo(() => {

        if (!reservationsAddons?.length) {
            return null;
        }
        return (
            <div
                className='details attached'
                style={
                    isMobileOnly ? { marginTop: '1em', overflow: 'visible' } : null
                }
            >
                <table className='addons-reservations-table'>
                    <thead>
                        <tr>
                            <td></td>
                            <RenderTitle label={`${text('Product')}/${text('Extra')}`} />
                            <RenderTitle label={`${text('Group')}/${text('Single')}`} />
                            <RenderTitle label={text('Price')} />
                            <RenderTitle label={text('AddonsQuantity')} />
                            <RenderTitle label={text('Pax')} />
                            <RenderTitle label={text('ResNum')} />
                        </tr>
                    </thead>
                    <tbody>
                        {reservationsAddons.map(reservationAddon => {
                            const reservation = reservations.find(x => x.Id === reservationAddon.ReservationId)
                            const reservationDailyAccommodation = reservationDailyAccommodations.find(x => x.ReservationId === reservationAddon.ReservationId)
                            return (
                                <tr key={reservationAddon.Id}>
                                    <td>
                                        <EditButton onClick={() => onOpenExtraAddonsPopup(reservation.Id)} />
                                    </td>
                                    <td>
                                        <Header size='tiny'>
                                            <Header.Content>
                                                {reservationAddon.ProductId ? text('Product') : text('Extra')}
                                            </Header.Content>
                                        </Header>
                                    </td>
                                    <td>
                                        <Header size='tiny'>
                                            <Header.Content>
                                                {isGroupReservation(reservation) ? text('Group') : text('Single')}
                                            </Header.Content>
                                        </Header>
                                    </td>
                                    <td>
                                        <Header size='tiny'>
                                            <Header.Content>
                                                {formatValueAsCurrency(formatAddonTotal(reservationAddon), hotel.MainCurrencyId)}
                                            </Header.Content>
                                        </Header></td>
                                    <td>
                                        <Header size='tiny'>
                                            <Header.Content>
                                                {SumQuantity(reservationAddon)}
                                            </Header.Content>
                                        </Header></td>
                                    <td>
                                        <Header size='tiny'>
                                            <Header.Content>
                                                {formatGuests(reservationDailyAccommodation)}
                                            </Header.Content>
                                        </Header>
                                    </td>
                                    <td>
                                        <Header size='tiny'>
                                            <Header.Content>
                                                <a
                                                    rel='noreferrer'
                                                    target='_blank'
                                                    href={`${endpoint}PMS/Reservations/Index/` + reservation.Id}>
                                                    {reservation.Number}
                                                </a>
                                            </Header.Content>
                                        </Header>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        )

    }, [reservations, reservationsAddons, reservationDailyAccommodations, reservationsGroups, hotel, currencyExchange])

    return (
        <React.Fragment>
            <Label attached='top'
                size='large'
                style={{
                    textAlign: 'right',
                    position: 'unset',
                    lineHeight: '100%'
                }}>
                <Icon
                    name='close'
                    style={{ float: 'right', fontSize: '1.2em' }}
                    link
                    onClick={() => props.hideSidebar()}
                />
            </Label>

            <div className="addon-details">

                <table>
                    <thead>
                        <tr>
                            <td>
                                <Header size='large'>
                                    <Header.Content>
                                        {text('ReservationsDetailsForAddon')}
                                    </Header.Content>
                                </Header>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {addon && (
                            <tr>
                                <td>
                                    <Header size='small'>
                                        <Header.Subheader>
                                            {text('AddonType')}
                                        </Header.Subheader>
                                        <Header.Content>
                                            {addon.Name}
                                        </Header.Content>
                                    </Header>
                                </td>
                            </tr>
                        )}
                        {props?.date && (
                            <tr>
                                <td>
                                    <Header size='small'>
                                        <Header.Subheader>
                                            {text('Date')}
                                        </Header.Subheader>
                                        <Header.Content>
                                            {moment(props.date).format(dateFormat)}
                                        </Header.Content>
                                    </Header>
                                </td>
                            </tr>
                        )}
                        {reservations?.length > 0 && hotel.MainCurrencyId && (
                            <tr>
                                <td>
                                    <Header size='small'>
                                        <Header.Subheader>
                                            {text('TotalIncome')}
                                        </Header.Subheader>
                                        <Header.Content>
                                            {formatValueAsCurrency(getTotal(), hotel.MainCurrencyId)}
                                        </Header.Content>
                                    </Header>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            {reservationsTable}
            <ExtraAddonsPopup
                open={isOpenExtraAddons}
                onClose={onCloseExtraAddonsPopup}
                reservation={selectedReservation}
                closeOnDimmerClick={true}
            // reservationDailyAddonsExist={setReservationDailyAddonsExist}
            />
        </React.Fragment>
    )
}

AddonReservationsDetails.propTypes = {
    date: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.instanceOf(Date),
    ]),
    chainAddonId: PropTypes.number,
    hotelId: PropTypes.number,
    hideSidebar: PropTypes.func,
    roomTypeIds: PropTypes.arrayOf(PropTypes.number),
}

export default AddonReservationsDetails
